import { Max } from '.';
import classNames from 'classnames';
import { CartItemViewModel } from '@/types/api';
import { IncreamentErrors } from '@/types/errors';
import { SupportedEvents, sendAddOrRemoveCartItemEvent } from '@/features/analytics/datalayer';
import { IconAdd, IconMinus, IconTrash } from '@/features/icons';
import BubblesLoader from '@/features/loading/bubbles-loader';
import usePostCartItem from '@/features/product-card/utils/use-post-cart-item';
import { Webengage } from '@/features/webengage';
import { SupportedEvents as WESupportedEvents } from '@/features/webengage/types';
import { getCartErrorMessage } from '@/app/(order)/cart/_utils/get-cart-error-message';
import { useGetProductInCart } from '@/app/(pdp)/_utils/use-get-product-in-cart';
import usePDPData from '@/app/(pdp)/_utils/use-pdp-data';
import { useAppCart } from '@/app/_utils/use-app-cart';
import { Binoban } from '@/utils/binoban';
import { AddToCartCounterProps } from './types';
import { PostCartTypes } from '../types';
export function AddToCartCounter({
  type,
  size = 'responsive',
  maxLimited,
  className,
  atpId,
  initialCount = 0
}: AddToCartCounterProps) {
  const {
    data
  } = usePDPData();
  const {
    productInCart
  } = useGetProductInCart(`${atpId}`);
  const {
    updateCart,
    cartInStorage: prevCart
  } = useAppCart();
  const {
    mutate: mutateCart,
    isPending,
    error
  } = usePostCartItem();
  const count = productInCart?.qty || initialCount;
  const isNoStockError = error && error.length ? error?.at(0)?.code === IncreamentErrors.NoStock : false;
  const isReachedToLimit = isNoStockError || count >= maxLimited;
  const decreaseCount = () => {
    if (!count) return;
    mutateCart({
      type: PostCartTypes.Decreament,
      productCartId: productInCart?.id || ''
    }, {
      onSuccess: cart => {
        updateCart(cart);
        const removedItems = prevCart?.items?.filter(prevItem => !cart?.items?.some(currentItem => currentItem.id === prevItem.id));
        const qtyChangedItems = prevCart?.items?.filter(prevItem => {
          const currentItem = cart?.items?.find(item => item.id === prevItem.id);
          return currentItem && currentItem.qty !== prevItem.qty;
        });
        prevCart && removedItems?.forEach(removedItem => {
          Webengage.trackCartAction({
            ...removedItem,
            qty: (removedItem.qty ?? 0) - 1
          }, type, WESupportedEvents.remove_from_cart, removedItem?.pricingGift ?? false);
        });
        prevCart && qtyChangedItems?.forEach(changedItem => {
          const currentItem = cart?.items?.find(item => item.id === changedItem.id);
          if (!currentItem || currentItem.qty === undefined || changedItem.qty === undefined) return;
          if (currentItem.qty < changedItem.qty) {
            Webengage.trackCartAction({
              ...changedItem,
              qty: changedItem.qty - 1
            }, type, WESupportedEvents.remove_from_cart, changedItem?.pricingGift ?? false);
          }
        });
        if (prevCart) {
          sendAddOrRemoveCartItemEvent(SupportedEvents.remove_from_cart, prevCart, cart, type);
        }
      },
      onError: error => {
        type === 'pdp' && getCartErrorMessage(error);
      }
    });
  };
  const increaseCount = () => {
    if (isReachedToLimit) return;
    mutateCart({
      type: PostCartTypes.Increament,
      productCartId: productInCart?.id || ''
    }, {
      onSuccess: cart => {
        updateCart(cart);
        if (prevCart) {
          sendAddOrRemoveCartItemEvent(SupportedEvents.add_to_cart, prevCart, cart, type);
        }
        const qtyChangedItems = prevCart?.items?.filter(prevItem => {
          const currentItem = cart?.items?.find(item => item.id === prevItem.id);
          return currentItem && currentItem.qty !== prevItem.qty;
        });
        const addedProduct = cart.items?.find((value: CartItemViewModel) => value.atpId === atpId);
        prevCart && qtyChangedItems?.forEach(changedItem => {
          const currentItem = cart?.items?.find(item => item.id === changedItem.id);
          if (!currentItem || currentItem.qty === undefined || changedItem.qty === undefined) return;
          Webengage.trackCartAction({
            ...currentItem,
            qty: currentItem.qty
          }, type, WESupportedEvents.add_to_cart, currentItem?.pricingGift ?? false);
        });
        Binoban.productAddedEvent(data?.id || '', data?.breadcrumb?.at(-1)?.slug || '', `${addedProduct?.vendorId ?? ''}-${atpId}`, addedProduct?.vendorId ?? '');
      },
      onError: error => {
        type === 'pdp' && getCartErrorMessage(error);
      }
    });
  };
  if (!count) return null;
  const iconClassName = classNames({
    'w-4 h-4 lg:w-6 lg:h-6': size === 'responsive',
    'w-6 h-6': size === 'large',
    'w-4 h-4': size === 'small'
  });
  return <div role='button' onClick={e => {
    e.preventDefault();
    e.stopPropagation();
  }} className={classNames('flex items-center justify-between rounded-lg border border-border-pink bg-surface-solid-0', {
    'h-8 w-22 px-2 lg:h-11 lg:w-30 lg:px-3': size === 'responsive',
    'h-11 w-30 px-3': size === 'large',
    'h-8 w-22 px-2': size === 'small',
    'py-[3px] text-caption-12 lg:py-[7px] lg:text-body-14': isReachedToLimit && size === 'responsive',
    'py-[7px] text-body-14': isReachedToLimit && size === 'large',
    'py-[3px] text-caption-12': isReachedToLimit && size === 'small',
    'py-1 text-body-14 lg:py-2.5 lg:text-body-16': !isReachedToLimit && size === 'responsive',
    'py-2.5 lg:text-body-16': !isReachedToLimit && size === 'large',
    'py-1 text-body-14': !isReachedToLimit && size === 'small'
  }, className)} data-sentry-component="AddToCartCounter" data-sentry-source-file="add-to-cart-counter.tsx">
            <button aria-label='Increase quantity' onClick={() => increaseCount()} className={'text-primary-main disabled:text-primary-shade-200'} disabled={isReachedToLimit || isPending}>
                <IconAdd className={iconClassName} data-sentry-element="IconAdd" data-sentry-source-file="add-to-cart-counter.tsx" />
            </button>

            {isPending ? <BubblesLoader color='pink' size='small' /> : <div className={classNames('flex cursor-default flex-col items-center justify-around gap-0.5', {
      'lg:justify-center': size === 'responsive',
      'justify-center': size === 'large'
    })}>
                    {isReachedToLimit && <Max />}

                    <span className={classNames('font-medium leading-6 text-primary-main', {
        '!leading-3': isReachedToLimit
      })}>
                        {count}
                    </span>
                </div>}

            <button aria-label='Decrease quantity' onClick={() => decreaseCount()} className='text-primary-main disabled:text-primary-shade-200' disabled={isPending}>
                {count > 1 ? <IconMinus className={iconClassName} /> : <IconTrash className={iconClassName} />}
            </button>
        </div>;
}